import { orderLegacyGoods, orderLegacyShipping } from './orderLegacy'

export interface OrderTotalsBreakdown {
  /** The goods before discounts; undefined for an order with no goods to value at all. */
  value?: number
  /** Each discount on the goods (negative), by its promotion's name; no name, a generic one. */
  discounts: { name?: string, amount: number }[]
  postage: number
  /**
   * Every other surcharge, those of nothing included (the old shop's free returns). `details` lets
   * `surchargeLabel` name an opt-in add-on by its own name.
   */
  charges: { name: string, price: number, details?: SurchargeDetails | null }[]
}

interface SurchargeDetails {
  name?: string | null
  isAddOn?: boolean | null
}

interface TotalsDiscount {
  adjustmentSource: string
  amount: number
  description?: string | null
}

/**
 * The rows of a placed order's totals above the total, in minor units; they add up to it.
 *
 * `subTotal` holds the goods with their discounts prorated in and every surcharge, so the value
 * takes the fees out and adds the discounts back, and both are then listed one by one. An order
 * migrated from the old shop is delivered by a surcharge, its postage, and may hold goods as
 * surcharges (utils/orderLegacy): those stay in the value, with the migration's rounding.
 * Checkout passes `legacySurcharges: false`: an unselected shipping method does not make a fee goods.
 */
export function orderTotalsBreakdown<Surcharge extends { sku?: string | null, name: string, price: number, details?: SurchargeDetails | null }>(order: {
  subTotal: number
  shipping: number
  couponCodes?: readonly string[] | null
  lines: readonly unknown[]
  discounts?: readonly TotalsDiscount[] | null
  shippingLines?: readonly { discounts?: readonly TotalsDiscount[] | null }[] | null
  surcharges?: readonly Surcharge[] | null
}, { legacySurcharges = true }: { legacySurcharges?: boolean } = {}): OrderTotalsBreakdown {
  // Vendure's `order.discounts` merges, per promotion, what it took off the goods with what it took
  // off shipping, but `shipping` is already the discounted price (a free-shipping promotion), so
  // each discount keeps only its goods part. A cancellation's CANCEL_ORDER discount sits on the
  // shipping line and drops out too; `shipping` carries it for a cancelled order, though not, as
  // the backend saves them today, for one rejected on delivery.
  const offShipping = new Map<string, number>()
  for (const line of order.shippingLines ?? []) {
    for (const discount of line.discounts ?? [])
      offShipping.set(discount.adjustmentSource, (offShipping.get(discount.adjustmentSource) ?? 0) + discount.amount)
  }
  // The migration gives a line the old shop repriced without a code a "Legacy discount" placeholder,
  // at times one that adds money: that is no discount, so it stays in the value.
  const placeholder = (discount: TotalsDiscount) => discount.adjustmentSource === 'LEGACY_COUPON' && !order.couponCodes?.length
  const discounts = (order.discounts ?? [])
    .map(discount => ({ discount, amount: discount.amount - (offShipping.get(discount.adjustmentSource) ?? 0) }))
    .filter(({ discount, amount }) => amount < 0 || (amount > 0 && !placeholder(discount)))
    .map(({ discount, amount }) => ({ name: placeholder(discount) ? undefined : discount.description?.trim() || undefined, amount }))

  const legacyShipping = legacySurcharges ? orderLegacyShipping(order) : undefined
  const legacyGoods = legacySurcharges ? orderLegacyGoods(order) : []
  const charges = (order.surcharges ?? [])
    .filter(surcharge => surcharge !== legacyShipping && !legacyGoods.includes(surcharge))
    .map(({ name, price, details }) => ({ name, price, details }))

  const sum = (amounts: number[]) => amounts.reduce((total, amount) => total + amount, 0)
  const value = order.subTotal - (legacyShipping?.price ?? 0) - sum(charges.map(charge => charge.price)) - sum(discounts.map(discount => discount.amount))
  return {
    value: order.lines.length || legacyGoods.length || value !== 0 ? value : undefined,
    discounts,
    postage: legacyShipping?.price ?? order.shipping,
    charges,
  }
}
